import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './technicianMap.css';
import {
    Box,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getTechnicianMapSnapshot, getTechnicianMapTrack, getTechnicianDayReport, downloadTechnicianDayPdf } from 'services/technicianMap';
import {
    STATUS_META,
    canAccessTechnicianMap,
    formatAlertSummary,
    formatClock,
    formatHourMinute,
    formatMinutes,
    formatSpeed,
    haversineMeters,
    isAtDepot,
    nearestUnit,
    splitTeams,
    todayYmd,
    trackSegments
} from './technicianMapUtils';

const BH = [-19.9167, -43.9345];
const TILE = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const LINE_CASING = {
    color: '#ffffff',
    weight: 10,
    opacity: 0.95,
    lineJoin: 'round',
    lineCap: 'round',
    pane: 'overlayPane'
};
const LINE_STROKE = {
    color: '#6E368C',
    weight: 6,
    opacity: 1,
    lineJoin: 'round',
    lineCap: 'round',
    pane: 'overlayPane'
};

const sameId = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase();
const normPlate = (value) => String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const markerIcon = (tech, selected = false) => {
    const color = STATUS_META[tech.status]?.color || STATUS_META.offline.color;
    const heading = Number(tech.point?.heading) || 0;
    const plate = tech.point?.license_plate || 'sem placa';
    if (selected) {
        return L.divIcon({
            className: 'tech-div-icon',
            iconSize: [220, 56],
            iconAnchor: [11, 28],
            html: `<div class="tech-marker-wrap is-selected">
                <div class="tech-marker-pulse" style="border-color:${color}"></div>
                <div class="tech-marker-pin" style="background:${color};transform:rotate(${heading}deg)"></div>
                <div class="tech-marker-label">
                    <strong>${tech.nome || 'Técnico'}</strong>
                    <span>${plate}</span>
                </div>
            </div>`
        });
    }
    return L.divIcon({
        className: 'tech-div-icon',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
        html: `<div class="tech-marker-wrap">
            <div class="tech-marker-pin" style="background:${color};transform:rotate(${heading}deg)"></div>
        </div>`
    });
};

const TechnicianMap = () => {
    const user = useSelector((state) => state.auth.user);
    const allowed = canAccessTechnicianMap(user);
    const mapNode = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef({});
    const circlesRef = useRef([]);
    const lineRef = useRef(null);
    const stopsRef = useRef([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [snapshot, setSnapshot] = useState(null);
    const [technicians, setTechnicians] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [reportDate, setReportDate] = useState(todayYmd());
    const [dayReport, setDayReport] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [trackLoading, setTrackLoading] = useState(false);
    const [wsState, setWsState] = useState('off');
    const [alertsOpen, setAlertsOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const selectedIdRef = useRef(null);
    const selectSeqRef = useRef(0);
    const techniciansRef = useRef([]);
    const selectedDriverIdRef = useRef(null);
    const reportDateRef = useRef(todayYmd());
    const trackLiveRef = useRef({
        latlngs: [],
        lastAt: 0,
        casing: null,
        line: null,
        end: null,
        group: null
    });
    const extendTrackRef = useRef(() => {});

    const radius = snapshot?.radius_meters || 500;
    const units = snapshot?.units || [];
    techniciansRef.current = technicians;
    reportDateRef.current = reportDate;

    const loadSnapshot = useCallback(async () => {
        setError('');
        setLoading(true);
        try {
            const response = await getTechnicianMapSnapshot();
            const data = response.data?.data || {};
            setSnapshot(data);
            setTechnicians(data.technicians || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Não foi possível carregar o mapa dos técnicos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (allowed) loadSnapshot();
    }, [allowed, loadSnapshot]);

    useEffect(() => {
        if (!mapNode.current || mapRef.current) return undefined;
        const map = L.map(mapNode.current, { zoomControl: false }).setView(BH, 12);
        L.tileLayer(TILE, {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
        }).addTo(map);
        L.control.zoom({ position: 'bottomright' }).addTo(map);
        mapRef.current = map;
        const resize = () => map.invalidateSize();
        setTimeout(resize, 80);
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
            map.remove();
            mapRef.current = null;
        };
    }, []);

    useEffect(() => {
        const map = mapRef.current;
        if (!map) return;
        circlesRef.current.forEach((layer) => layer.remove());
        circlesRef.current = units.map((unit) =>
            L.circle([unit.latitude, unit.longitude], {
                radius,
                color: '#6E368C',
                weight: 1,
                fillColor: '#9B6BB8',
                fillOpacity: 0.12
            })
                .bindTooltip(unit.nome, { direction: 'top' })
                .addTo(map)
        );
    }, [units, radius]);

    const fittedRef = useRef(false);
    const onSelectRef = useRef(null);

    const upsertMarker = useCallback((tech) => {
        const map = mapRef.current;
        if (!map || !tech?.point) return;
        const key = String(tech.driver_id);
        const latlng = [tech.point.lat, tech.point.lng];
        const selected = selectedIdRef.current === tech.tecnico_id;
        const icon = markerIcon(tech, selected);
        const plate = tech.point.license_plate || 'sem placa';
        const html = `<strong>${tech.nome}</strong><br/>${plate} · ${formatSpeed(
            tech.point.speed
        )}<br/>${STATUS_META[tech.status]?.label || ''} · ${formatClock(tech.point.position_at)}`;
        let marker = markersRef.current[key];
        if (marker) {
            marker.setLatLng(latlng).setIcon(icon).setPopupContent(html);
        } else {
            marker = L.marker(latlng, { icon }).bindPopup(html).addTo(map);
            markersRef.current[key] = marker;
        }
        marker.setZIndexOffset(selected ? 2000 : 0);
        marker.unbindTooltip();
        if (selected && typeof marker.bringToFront === 'function') {
            marker.bringToFront();
        }
        marker.off('click').on('click', () => onSelectRef.current?.(tech));
    }, []);

    const paintTrack = useCallback((segments, { fit = true } = {}) => {
        const map = mapRef.current;
        const parts = Array.isArray(segments?.[0]?.[0]) ? segments : segments?.length ? [segments] : [];
        const latlngs = parts.flat();
        if (!map || !latlngs.length) return false;
        if (lineRef.current) {
            lineRef.current.remove();
            lineRef.current = null;
        }
        const group = L.layerGroup();
        let casing = null;
        let line = null;
        let end = null;
        let lastSegment = [];
        parts.forEach((segment) => {
            if (!segment.length) return;
            lastSegment = segment.map((pair) => [Number(pair[0]), Number(pair[1])]);
            if (segment.length < 2) return;
            casing = L.polyline(lastSegment, LINE_CASING).addTo(group);
            line = L.polyline(lastSegment, LINE_STROKE).addTo(group);
        });
        if (latlngs.length >= 1) {
            L.circleMarker(latlngs[0], {
                radius: 8,
                color: '#ffffff',
                weight: 2,
                fillColor: '#2E7D32',
                fillOpacity: 1
            })
                .bindTooltip('Início do trajeto', { direction: 'top' })
                .addTo(group);
            end = L.circleMarker(latlngs[latlngs.length - 1], {
                radius: 8,
                color: '#ffffff',
                weight: 2,
                fillColor: '#C62828',
                fillOpacity: 1
            })
                .bindTooltip(
                    reportDateRef.current === todayYmd() ? 'Posição atual' : 'Fim do trajeto',
                    { direction: 'top' }
                )
                .addTo(group);
        }
        group.addTo(map);
        lineRef.current = group;
        trackLiveRef.current = {
            latlngs: lastSegment.length ? lastSegment : latlngs.map((pair) => [pair[0], pair[1]]),
            lastAt: 0,
            casing,
            line,
            end,
            group
        };
        if (fit && latlngs.length >= 2) {
            map.stop();
            map.fitBounds(L.latLngBounds(latlngs).pad(0.18), { animate: true, maxZoom: 16 });
        }
        return true;
    }, []);

    const extendSelectedTrack = useCallback((point, driverId) => {
        if (!point || reportDateRef.current !== todayYmd()) return;
        if (!sameId(selectedDriverIdRef.current, driverId)) return;
        const lat = Number(point.lat ?? point.latitude);
        const lng = Number(point.lng ?? point.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;

        const map = mapRef.current;
        const state = trackLiveRef.current;
        const next = [lat, lng];
        const at = Date.parse(point.position_at || '') || Date.now();
        if (state.lastAt && at + 2000 < state.lastAt) return;

        const prev = state.latlngs[state.latlngs.length - 1];
        if (prev) {
            const meters = haversineMeters(prev[0], prev[1], lat, lng);
            const dt = state.lastAt ? Math.max(0, (at - state.lastAt) / 1000) : 0;
            const impliedKmh = dt > 0 ? (meters / dt) * 3.6 : 0;
            const teleport = meters > 80000 || (dt > 0 && meters > 400 && impliedKmh > 160);
            if (meters < 8) {
                state.latlngs[state.latlngs.length - 1] = next;
                state.lastAt = Math.max(state.lastAt || 0, at);
                if (state.casing) state.casing.setLatLngs(state.latlngs.slice());
                if (state.line) state.line.setLatLngs(state.latlngs.slice());
                if (state.end) state.end.setLatLng(next);
                return;
            }
            if (teleport) {
                state.latlngs = [next];
                state.casing = null;
                state.line = null;
                state.lastAt = at;
                if (state.end) state.end.setLatLng(next);
                return;
            }
        }

        if (!state.group && map) {
            paintTrack([prev ? [prev, next] : [next]], { fit: false });
            trackLiveRef.current.lastAt = at;
            return;
        }

        state.latlngs.push(next);
        state.lastAt = at;
        if (state.line && state.casing) {
            state.casing.setLatLngs(state.latlngs.slice());
            state.line.setLatLngs(state.latlngs.slice());
        } else if (state.latlngs.length >= 2 && state.group) {
            state.casing = L.polyline(state.latlngs, LINE_CASING).addTo(state.group);
            state.line = L.polyline(state.latlngs, LINE_STROKE).addTo(state.group);
        } else {
            paintTrack([state.latlngs], { fit: false });
            trackLiveRef.current.lastAt = at;
            return;
        }
        if (state.end) {
            state.end.setLatLng(next);
            state.end.bringToFront?.();
        }
        if (map && selectedIdRef.current) {
            const bounds = map.getBounds().pad(-0.18);
            if (bounds.isValid() && !bounds.contains(next)) {
                map.panTo(next, { animate: true, duration: 0.4 });
            }
        }
    }, [paintTrack]);

    extendTrackRef.current = extendSelectedTrack;

    useEffect(() => {
        technicians.forEach(upsertMarker);
        const map = mapRef.current;
        const withPoint = technicians.filter((tech) => tech.point);
        if (map && withPoint.length && !fittedRef.current) {
            const bounds = L.latLngBounds(withPoint.map((tech) => [tech.point.lat, tech.point.lng]));
            units.forEach((unit) => bounds.extend([unit.latitude, unit.longitude]));
            if (bounds.isValid()) {
                map.fitBounds(bounds.pad(0.18));
                fittedRef.current = true;
            }
        }
    }, [technicians, units, upsertMarker, selectedId]);

    const driverIds = useMemo(
        () => technicians.map((tech) => tech.driver_id).filter(Boolean),
        [technicians]
    );

    useEffect(() => {
        const url = process.env.REACT_APP_TELEMETRY_WS_URL || snapshot?.ws_url;
        if (!url || !driverIds.length) return undefined;
        let closed = false;
        let ping;
        const ws = new WebSocket(url);
        setWsState('connecting');
        ws.onopen = () => {
            if (closed) return;
            setWsState('live');
            driverIds.forEach((id) => ws.send(JSON.stringify({ action: 'subscribe', driver_id: id })));
            techniciansRef.current.forEach((tech) => {
                const plate = tech.point?.license_plate;
                if (plate) ws.send(JSON.stringify({ action: 'subscribe', plate }));
            });
            ping = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ action: 'ping' }));
                }
            }, 25000);
        };
        ws.onclose = () => {
            if (!closed) setWsState('off');
        };
        ws.onerror = () => {
            if (!closed) setWsState('error');
        };
        ws.onmessage = (event) => {
            let msg;
            try {
                msg = JSON.parse(event.data);
            } catch {
                return;
            }
            if (msg.type !== 'telemetry' || !msg.point) return;
            const driver = String(msg.norm_driver || msg.point.driver_id || '').trim().toUpperCase();
            const plate = normPlate(msg.norm_plate || msg.point.license_plate);
            setTechnicians((current) =>
                current.map((tech) => {
                    const sameDriver = driver && sameId(tech.driver_id, driver);
                    const samePlate = plate && normPlate(tech.point?.license_plate) === plate;
                    if (!sameDriver && !samePlate) return tech;
                    const point = {
                        lat: msg.point.lat,
                        lng: msg.point.lng,
                        position_at: msg.point.position_at,
                        speed: msg.point.speed,
                        heading: msg.point.heading,
                        ignition: msg.point.ignition,
                        license_plate: msg.point.license_plate
                    };
                    const speed = Number(point.speed || 0);
                    const moving = speed > 5 && point.ignition !== false;
                    const near = nearestUnit(point, units);
                    const atDepot = isAtDepot(point.lat, point.lng, snapshot?.depot);
                    const outside = atDepot ? false : near ? near.distance_meters > radius : true;
                    let status = moving ? 'moving' : 'stopped';
                    let alert = moving ? null : tech.alert;
                    if (!moving && outside && (tech.idle_minutes || 0) >= (snapshot?.stop_alert_minutes || 10)) {
                        status = 'alert';
                    }
                    const next = { ...tech, point, nearest_unit: near, status, alert };
                    upsertMarker(next);
                    extendTrackRef.current?.(point, tech.driver_id);
                    return next;
                })
            );
        };
        return () => {
            closed = true;
            if (ping) clearInterval(ping);
            ws.close();
        };
    }, [snapshot?.ws_url, snapshot?.stop_alert_minutes, driverIds.join('|'), units, radius, upsertMarker]);

    const selectTechnician = useCallback(async (tech, dateOverride) => {
        const date = dateOverride || reportDate;
        const seq = ++selectSeqRef.current;
        const prevId = selectedIdRef.current;
        selectedIdRef.current = tech.tecnico_id;
        selectedDriverIdRef.current = tech.driver_id;
        setSelectedId(tech.tecnico_id);
        setDayReport(null);
        setTrackLoading(true);
        if (prevId && prevId !== tech.tecnico_id) {
            const prev = techniciansRef.current.find((item) => item.tecnico_id === prevId);
            if (prev) upsertMarker(prev);
        }
        upsertMarker(tech);

        const map = mapRef.current;
        if (map) {
            map.stop();
            if (tech.point) {
                map.setView([tech.point.lat, tech.point.lng], Math.max(map.getZoom() || 12, 13), { animate: false });
            }
        }

        const clearTrack = () => {
            if (lineRef.current) {
                lineRef.current.remove();
                lineRef.current = null;
            }
            trackLiveRef.current = { latlngs: [], lastAt: 0, casing: null, line: null, end: null, group: null };
            stopsRef.current.forEach((layer) => layer.remove());
            stopsRef.current = [];
        };

        const drawTrack = (items) => paintTrack(trackSegments(items), { fit: true });

        const minStopSeconds = (snapshot?.map_stop_minutes || 5) * 60;

        const stopIcon = (minutes, outside) => {
            const color = outside ? '#C62828' : '#ED6C02';
            const label = minutes >= 60
                ? `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`
                : `${minutes} min`;
            return L.divIcon({
                className: 'stop-div-icon',
                iconSize: [86, 46],
                iconAnchor: [16, 24],
                html: `<div class="stop-marker">
                    <div class="stop-marker-halo" style="background:${color}"></div>
                    <div class="stop-marker-pin" style="background:${color}"></div>
                    <div class="stop-marker-badge" style="background:${color}">${label}</div>
                </div>`
            });
        };

        const drawStops = (stops = []) => {
            if (!map) return;
            stopsRef.current.forEach((layer) => layer.remove());
            stopsRef.current = [];
            stops
                .filter((stop) => (Number(stop.duration_seconds) || 0) >= minStopSeconds)
                .forEach((stop) => {
                    const lat = Number(stop.latitude ?? stop.lat);
                    const lng = Number(stop.longitude ?? stop.lng);
                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
                    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
                    if (isAtDepot(lat, lng, snapshot?.depot)) return;
                    const minutes = Math.max(1, Math.round((Number(stop.duration_seconds) || 0) / 60));
                    const near =
                        stop.nearest_unit || nearestUnit({ lat, lng }, units);
                    const outside =
                        stop.outside_unit ?? (!near || near.distance_meters > radius);
                    const color = outside ? '#C62828' : '#ED6C02';
                    const halo = L.circle([lat, lng], {
                        radius: 50,
                        color,
                        weight: 2,
                        fillColor: color,
                        fillOpacity: 0.2,
                        pane: 'overlayPane'
                    }).addTo(map);
                    const marker = L.marker([lat, lng], {
                        icon: stopIcon(minutes, outside),
                        zIndexOffset: 900
                    })
                        .bindTooltip(
                            `Parado ${formatMinutes(minutes)} · ${formatHourMinute(
                                stop.started_at
                            )}–${formatHourMinute(stop.ended_at)}${
                                near ? ` · ${near.nome}` : ''
                            }${outside ? ' · fora da unidade' : ''}${
                                stop.address ? ` · ${stop.address}` : ''
                            }`,
                            { direction: 'top', offset: [0, -10], className: 'stop-tooltip' }
                        )
                        .addTo(map);
                    stopsRef.current.push(halo, marker);
                });
        };

        clearTrack();
        try {
            const trackResponse = await getTechnicianMapTrack(tech.driver_id, date);
            if (seq !== selectSeqRef.current) return;
            const trackData = trackResponse.data?.data || {};
            const drawn = drawTrack(trackData.points || []);
            if (date === todayYmd()) {
                const latest =
                    techniciansRef.current.find((item) => item.tecnico_id === tech.tecnico_id) || tech;
                if (latest.point) extendSelectedTrack(latest.point, latest.driver_id);
            }
            if (!drawn && map && tech.point) {
                map.flyTo([tech.point.lat, tech.point.lng], 16, { duration: 0.4 });
            }
            drawStops(trackData.stops || []);
            setTrackLoading(false);
            getTechnicianDayReport(tech.driver_id, date)
                .then((reportResponse) => {
                    if (seq !== selectSeqRef.current) return;
                    const report = reportResponse?.data?.data || null;
                    if (report) setDayReport(report);
                    const liveLen = trackLiveRef.current.latlngs?.length || 0;
                    if (!drawn && report?.track_preview && liveLen < 2) {
                        drawTrack(report.track_preview);
                    }
                    if (Array.isArray(report?.stops) && report.stops.length) {
                        drawStops(report.stops);
                    }
                    if (date === todayYmd()) {
                        const latest =
                            techniciansRef.current.find((item) => item.tecnico_id === tech.tecnico_id) ||
                            tech;
                        if (latest.point) extendSelectedTrack(latest.point, latest.driver_id);
                    }
                })
                .catch(() => {});
        } catch {
            if (seq !== selectSeqRef.current) return;
            setTrackLoading(false);
            if (map && tech.point) {
                map.flyTo([tech.point.lat, tech.point.lng], 15, { duration: 0.5 });
            }
        }
    }, [reportDate, upsertMarker, units, radius, snapshot?.map_stop_minutes, snapshot?.depot, paintTrack, extendSelectedTrack]);

    onSelectRef.current = selectTechnician;

    const downloadPdf = async (tech) => {
        if (!tech?.driver_id) return;
        setPdfLoading(true);
        try {
            const blob = await downloadTechnicianDayPdf(tech.driver_id, reportDate);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `relatorio-${tech.nome}-${reportDate}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            setError('Não foi possível gerar o PDF do dia.');
        } finally {
            setPdfLoading(false);
        }
    };

    const selectedTech = technicians.find((item) => item.tecnico_id === selectedId);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return technicians.filter((tech) => {
            if (statusFilter && tech.status !== statusFilter) return false;
            if (!term) return true;
            const teams = splitTeams(tech.equipe).join(' ').toLowerCase();
            return (
                String(tech.nome || '').toLowerCase().includes(term) ||
                teams.includes(term) ||
                String(tech.point?.license_plate || '').toLowerCase().includes(term)
            );
        });
    }, [technicians, search, statusFilter]);

    const counts = useMemo(
        () =>
            technicians.reduce(
                (acc, tech) => {
                    acc[tech.status] = (acc[tech.status] || 0) + 1;
                    return acc;
                },
                { moving: 0, stopped: 0, alert: 0, offline: 0 }
            ),
        [technicians]
    );

    const alerts = technicians.filter((tech) => tech.status === 'alert' || tech.alert);

    if (!allowed) {
        return <Navigate to="/index" replace />;
    }

    const wsMeta =
        wsState === 'live'
            ? { label: 'Ao vivo', color: '#4ADE80' }
            : wsState === 'connecting'
              ? { label: 'Conectando', color: '#FBBF24' }
              : wsState === 'error'
                ? { label: 'WS falhou', color: '#FB7185' }
                : { label: 'Offline', color: '#94A3B8' };

    return (
        <Box sx={{ height: 'calc(100vh - 110px)', minHeight: 560, display: 'flex', gap: 1.5, m: { xs: -1, md: -2 } }}>
            <Box
                sx={{
                    width: { xs: '100%', md: 380 },
                    maxWidth: 420,
                    display: { xs: selectedId ? 'none' : 'flex', md: 'flex' },
                    flexDirection: 'column',
                    minHeight: 0,
                    bgcolor: '#10141F',
                    color: '#fff',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 18px 40px rgba(15,23,42,.25)'
                }}
            >
                <Box sx={{ p: 2, pb: 1.5, flexShrink: 0, background: 'linear-gradient(160deg,#7B3FA8 0%,#2A1638 72%)' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 22, lineHeight: 1.15 }}>
                                Técnicos ao vivo
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.6 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: wsMeta.color, flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.82)' }} noWrap>
                                    {wsMeta.label} · drivers Frotas
                                </Typography>
                            </Stack>
                        </Box>
                        <Tooltip title="Atualizar">
                            <IconButton onClick={loadSnapshot} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,.1)' }} size="small">
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,.72)', mt: 1.5, mb: 0.6, fontWeight: 600 }}>
                        Dia do trajeto
                    </Typography>
                    <TextField
                        type="date"
                        fullWidth
                        size="small"
                        hiddenLabel
                        className="tech-map-field"
                        value={reportDate}
                        inputProps={{
                            'aria-label': 'Dia do trajeto',
                            title: 'Dia do trajeto'
                        }}
                        onChange={(event) => {
                            const value = event.target.value;
                            setReportDate(value);
                            if (selectedTech) selectTechnician(selectedTech, value);
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#fff',
                                color: '#1E1430',
                                borderRadius: 2,
                                '& fieldset': { border: '1px solid rgba(255,255,255,.28)' }
                            },
                            '& .MuiOutlinedInput-input': {
                                color: '#1E1430',
                                WebkitTextFillColor: '#1E1430',
                                bgcolor: '#fff',
                                fontFamily: 'inherit',
                                fontSize: 14,
                                fontWeight: 600,
                                py: 1
                            },
                            '& input[type=date]': {
                                colorScheme: 'light'
                            }
                        }}
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,.72)', mt: 1.15, mb: 0.6, fontWeight: 600 }}>
                        Buscar técnico
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        hiddenLabel
                        className="tech-map-field"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Nome, equipe ou placa"
                        inputProps={{ 'aria-label': 'Buscar técnico por nome, equipe ou placa' }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#fff',
                                color: '#1E1430',
                                borderRadius: 2,
                                '& fieldset': { border: '1px solid rgba(255,255,255,.28)' }
                            },
                            '& .MuiOutlinedInput-input': {
                                color: '#1E1430',
                                WebkitTextFillColor: '#1E1430',
                                bgcolor: '#fff',
                                fontFamily: 'inherit',
                                fontSize: 14,
                                fontWeight: 600,
                                py: 1,
                                '&::placeholder': {
                                    color: 'rgba(30,20,48,.52)',
                                    WebkitTextFillColor: 'rgba(30,20,48,.52)',
                                    opacity: 1,
                                    fontWeight: 500
                                }
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#6E368C' }} />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Box
                        sx={{
                            mt: 1.25,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                            gap: 0.6
                        }}
                    >
                        {Object.keys(STATUS_META).map((key) => {
                            const active = statusFilter === key;
                            return (
                                <Box
                                    key={key}
                                    onClick={() => setStatusFilter(active ? '' : key)}
                                    sx={{
                                        px: 0.5,
                                        py: 0.7,
                                        borderRadius: 1.5,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        bgcolor: active ? STATUS_META[key].color : 'rgba(255,255,255,.1)',
                                        border: active ? '1px solid transparent' : '1px solid rgba(255,255,255,.08)',
                                        '&:hover': { bgcolor: active ? STATUS_META[key].color : 'rgba(255,255,255,.16)' }
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.1, color: '#fff' }}>
                                        {counts[key] || 0}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            color: 'rgba(255,255,255,.82)',
                                            lineHeight: 1.15,
                                            mt: 0.2
                                        }}
                                        noWrap
                                    >
                                        {STATUS_META[key].short}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {selectedId && dayReport && (
                    <Box sx={{ px: 1.5, pt: 1.25, flexShrink: 0 }}>
                        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.06)' }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.55)', letterSpacing: 0.5, fontWeight: 700 }}>
                                Trajeto · {reportDate.split('-').reverse().join('/')}
                            </Typography>
                            <Box
                                sx={{
                                    mt: 1,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                                    gap: 0.6
                                }}
                            >
                                {[
                                    { value: `${Number(dayReport.kpis?.distance_km || 0).toLocaleString('pt-BR')}`, label: 'km' },
                                    { value: formatMinutes(dayReport.kpis?.moving_minutes), label: 'mov.' },
                                    { value: dayReport.kpis?.units_visited || 0, label: 'unid.' },
                                    { value: dayReport.kpis?.stop_count || 0, label: 'paradas' }
                                ].map((kpi) => (
                                    <Box key={kpi.label} sx={{ minWidth: 0, textAlign: 'center' }}>
                                        <Typography noWrap sx={{ fontWeight: 800, fontSize: 13, color: '#fff' }}>
                                            {kpi.value}
                                        </Typography>
                                        <Typography noWrap sx={{ fontSize: 10, color: 'rgba(255,255,255,.55)' }}>
                                            {kpi.label}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            {dayReport.vehicle?.primary_plate && (
                                <Typography variant="caption" display="block" noWrap sx={{ color: 'rgba(255,255,255,.7)', mt: 0.8 }}>
                                    {dayReport.vehicle.plates?.join(' · ')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {alerts.length > 0 && (
                    <Box sx={{ px: 1.5, pt: 1.25, flex: '0 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <Box
                            onClick={() => setAlertsOpen((open) => !open)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                cursor: 'pointer',
                                bgcolor: 'rgba(198,40,40,.22)',
                                border: '1px solid rgba(255,138,128,.35)',
                                flexShrink: 0
                            }}
                        >
                            <Box
                                sx={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    bgcolor: '#C62828',
                                    color: '#fff',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                {alerts.length}
                            </Box>
                            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: '#FFCDD2' }} noWrap>
                                Parados fora da unidade
                            </Typography>
                            {alertsOpen ? <ExpandLessIcon sx={{ color: '#FFCDD2' }} /> : <ExpandMoreIcon sx={{ color: '#FFCDD2' }} />}
                        </Box>
                        {alertsOpen && (
                            <Box sx={{ overflow: 'auto', maxHeight: 148, mt: 0.75 }}>
                                {alerts.map((tech) => (
                                    <Box
                                        key={`alert-${tech.tecnico_id}`}
                                        onClick={() => selectTechnician(tech)}
                                        sx={{
                                            p: 1,
                                            mb: 0.5,
                                            borderRadius: 1.5,
                                            cursor: 'pointer',
                                            bgcolor: 'rgba(198,40,40,.16)',
                                            '&:hover': { bgcolor: 'rgba(198,40,40,.28)' }
                                        }}
                                    >
                                        <Typography sx={{ color: '#FFCDD2', fontWeight: 700, fontSize: 12 }} noWrap>
                                            {tech.nome}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,.7)', fontSize: 11 }} noWrap>
                                            {formatAlertSummary(tech)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 1.5, pt: 1.25 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: '#C4A3DE' }} />
                        </Box>
                    ) : filtered.length === 0 ? (
                        <Typography sx={{ color: 'rgba(255,255,255,.6)', p: 2, fontSize: 13 }}>
                            {statusFilter || search
                                ? 'Nenhum técnico neste filtro.'
                                : 'Nenhum técnico cadastrado como motorista ativo no Frotas (e-mail + iButton).'}
                        </Typography>
                    ) : (
                        filtered.map((tech) => {
                            const selected = selectedId === tech.tecnico_id;
                            const teams = splitTeams(tech.equipe);
                            const extraTeams = Math.max(0, teams.length - 1);
                            const status = STATUS_META[tech.status] || STATUS_META.offline;
                            const plate = tech.point?.license_plate || 'sem placa';
                            const unitLabel = tech.nearest_unit
                                ? `${tech.nearest_unit.nome} · ${tech.nearest_unit.distance_meters} m`
                                : 'Fora de unidade';
                            return (
                                <Box
                                    key={tech.tecnico_id}
                                    onClick={() => selectTechnician(tech)}
                                    sx={{
                                        p: 1.25,
                                        mb: 1,
                                        borderRadius: 2,
                                        cursor: 'pointer',
                                        bgcolor: selected ? 'rgba(123,63,168,.38)' : 'rgba(255,255,255,.04)',
                                        border: selected ? '1px solid rgba(196,163,222,.8)' : '1px solid rgba(255,255,255,.05)',
                                        '&:hover': { bgcolor: selected ? 'rgba(123,63,168,.48)' : 'rgba(255,255,255,.08)' }
                                    }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="flex-start">
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: status.color,
                                                mt: 0.7,
                                                flexShrink: 0,
                                                boxShadow: `0 0 0 4px ${status.color}22`
                                            }}
                                        />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <Typography noWrap sx={{ fontWeight: 800, fontSize: 14, minWidth: 0, flex: 1 }}>
                                                    {tech.nome}
                                                </Typography>
                                                <Tooltip title="PDF do dia">
                                                    <IconButton
                                                        size="small"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            downloadPdf(tech);
                                                        }}
                                                        sx={{ color: 'rgba(255,255,255,.75)', p: 0.4 }}
                                                        disabled={pdfLoading}
                                                    >
                                                        <PictureAsPdfIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.45, minWidth: 0 }}>
                                                <Chip
                                                    size="small"
                                                    label={status.short}
                                                    sx={{
                                                        bgcolor: status.color,
                                                        color: '#fff',
                                                        height: 20,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <Chip
                                                    size="small"
                                                    label={plate}
                                                    sx={{
                                                        height: 20,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        bgcolor: 'rgba(255,255,255,.1)',
                                                        color: '#fff',
                                                        flexShrink: 0,
                                                        '& .MuiChip-label': { px: 0.8, fontFamily: 'ui-monospace, monospace' }
                                                    }}
                                                />
                                                {teams[0] && (
                                                    <Tooltip title={teams.join(' · ')}>
                                                        <Chip
                                                            size="small"
                                                            label={extraTeams ? `${teams[0]} +${extraTeams}` : teams[0]}
                                                            sx={{
                                                                height: 20,
                                                                fontSize: 10,
                                                                maxWidth: 140,
                                                                bgcolor: 'rgba(255,255,255,.08)',
                                                                color: 'rgba(255,255,255,.88)',
                                                                '& .MuiChip-label': { px: 0.8, overflow: 'hidden', textOverflow: 'ellipsis' }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                            <Typography noWrap sx={{ color: 'rgba(255,255,255,.58)', fontSize: 11, mt: 0.55 }}>
                                                {formatSpeed(tech.point?.speed)} · {formatClock(tech.point?.position_at)} · {unitLabel}
                                            </Typography>
                                            {tech.alert && (
                                                <Typography noWrap sx={{ color: '#FF8A80', fontSize: 11, mt: 0.35, fontWeight: 600 }}>
                                                    {formatAlertSummary(tech)}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>

            <Box sx={{ flex: 1, position: 'relative', borderRadius: 3, overflow: 'hidden', minWidth: 0 }}>
                <Box ref={mapNode} sx={{ position: 'absolute', inset: 0 }} />
                {trackLoading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 16,
                            left: 16,
                            zIndex: 500,
                            bgcolor: 'rgba(15,23,42,.88)',
                            color: '#fff',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <CircularProgress size={16} sx={{ color: '#C4A3DE' }} />
                        <Typography variant="caption">Carregando trajeto…</Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 500,
                        bgcolor: 'rgba(15,23,42,.88)',
                        color: '#fff',
                        px: 2,
                        py: 1,
                        borderRadius: 2
                    }}
                >
                    <Typography variant="caption" display="block">Raio da unidade: {radius} m</Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                        Parada &gt; {snapshot?.map_stop_minutes || 5} min no mapa
                    </Typography>
                </Box>
                {selectedTech && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 24,
                            left: 16,
                            zIndex: 500,
                            bgcolor: '#6E368C',
                            color: '#fff',
                            px: 2,
                            py: 1.25,
                            borderRadius: 2,
                            boxShadow: '0 12px 28px rgba(42,22,56,.35)',
                            maxWidth: 320
                        }}
                    >
                        <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 0.6 }}>
                            VEÍCULO SELECIONADO
                        </Typography>
                        <Typography sx={{ fontWeight: 800, lineHeight: 1.25 }} noWrap>
                            {selectedTech.nome}
                        </Typography>
                        <Typography variant="caption" display="block" noWrap>
                            {selectedTech.point?.license_plate || 'sem placa no momento'}
                            {trackLoading ? ' · buscando trajeto' : ''}
                        </Typography>
                    </Box>
                )}
                {selectedId && (
                    <IconButton
                        onClick={() => {
                            const tech = technicians.find((item) => item.tecnico_id === selectedId);
                            if (tech?.point && mapRef.current) {
                                mapRef.current.flyTo([tech.point.lat, tech.point.lng], 16);
                            }
                        }}
                        sx={{ position: 'absolute', bottom: 88, right: 16, zIndex: 500, bgcolor: '#fff' }}
                    >
                        <MyLocationIcon />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

export default TechnicianMap;
